// src/pages/SearchBundlePage.tsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  List,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Alert,
  Chip,
  Grid,
  ListItemButton,
  useTheme,
  styled,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PublicIcon from "@mui/icons-material/Public";
import DomainIcon from "@mui/icons-material/Domain";
import PersonIcon from "@mui/icons-material/Person";

import axios, { type RawAxiosRequestHeaders } from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../services/api";

import type { FolderRow } from "../types/folder";
import type { FileRow } from "../types/file";
import type { ApiResponse } from "../types/api-response";

// ---------------- UTIL --------------------

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ---------------- STYLED COMPONENTS --------------------

const ScrollHiddenBox = styled(Box)({
  flexGrow: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': { display: 'none' },
  '-ms-overflow-style': 'none',
  'scrollbar-width': 'none',
});

// ---------------- TYPES --------------------

type FolderNode = FolderRow & { children: FolderNode[] };

// ✅ [최적화] 백엔드에서 폴더와 파일을 한 번에 내려준다고 가정
interface FolderWithFiles extends FolderRow {
  files: FileRow[];
}

interface SearchBundleListRow {
  bundle_id: string;
  org_id: string;
  name: string;
  description: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  folder_count: number;
  file_count: number;
  is_editable: boolean;
}

interface SearchBundleResourceRow {
  resource_type: "folder" | "file";
  folder_id: string | null;
  file_id: string | null;
  folder_name?: string | null;
  file_name?: string | null;
}

interface SearchBundleDetailResponse {
  bundle: {
    id?: string;
    bundle_id?: string;
    org_id: string;
    name: string;
    description: string | null;
    created_by: string | null;
    created_by_name: string | null;
    created_at: string;
    updated_at: string;
  };
  resources: SearchBundleResourceRow[];
}

// ---------------- PAGE --------------------

const SearchBundlePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // --- User Info ---
  const [userId, setUserId] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [tier, setTier] = useState<number | null>(null);

  // --- Folder Data (3-way split) ---
  const [globalFolders, setGlobalFolders] = useState<FolderRow[]>([]);
  const [orgFolders, setOrgFolders] = useState<FolderRow[]>([]);
  const [myFolders, setMyFolders] = useState<FolderRow[]>([]);
  
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState<string | null>(null);
  const [filesMap, setFilesMap] = useState<Record<string, FileRow[]>>({});

  // --- Bundle List ---
  const [bundles, setBundles] = useState<SearchBundleListRow[]>([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [bundleError, setBundleError] = useState<string | null>(null);

  // --- Editor State ---
  const [editingBundleId, setEditingBundleId] = useState<string | null>(null);
  const [bundleName, setBundleName] = useState("");
  const [bundleDescription, setBundleDescription] = useState("");
  const [bundleEditable, setBundleEditable] = useState(true);

  // Selection
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(() => new Set());
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(() => new Set());

  // Accordion State
  const [expandGlobal, setExpandGlobal] = useState(true);
  const [expandOrg, setExpandOrg] = useState(true);
  const [expandMy, setExpandMy] = useState(true);

  // --- Auth Headers ---
  const getHeaders = useCallback((): RawAxiosRequestHeaders => {
    const user_id = localStorage.getItem("user_id") || "";
    const access_token = localStorage.getItem("access_token") || "";
    const refresh_token = localStorage.getItem("refresh_token") || "";
    return {
      "x-user-id": user_id,
      "x-access-token": access_token,
      "x-refresh-token": refresh_token,
    };
  }, []);

  // --- Initialization ---
  useEffect(() => {
    const _user = localStorage.getItem("user_id");
    const _org = localStorage.getItem("org_id");
    const _tier = localStorage.getItem("tier");
    setUserId(_user);
    setOrgId(_org);
    setTier(_tier ? Number(_tier) : null);
  }, []);

  const isOrgAdmin = tier === 0;
  const isGlobalOrg = orgId === '#GLOBAL#';
  const isSuperAdmin = isGlobalOrg && isOrgAdmin; // #GLOBAL# 조직의 관리자

  // --- Load Bundles ---
  const loadBundles = useCallback(async () => {
    setBundleError(null);
    setLoadingBundles(true);
    try {
      const res = await axios.get<ApiResponse<SearchBundleListRow[]>>(
        `${API_BASE}search-bundles`,
        { headers: getHeaders() }
      );
      if (!res.data.success) {
        throw new Error(res.data.error || "Failed to load bundles");
      }
      setBundles(res.data.data || []);
    } catch (e: any) {
      setBundleError(e?.message || "Failed to load bundles");
      setBundles([]);
    } finally {
      setLoadingBundles(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    loadBundles();
  }, [loadBundles]);

  // --- Load Folders & Files (Optimized) ---
  const loadAllFolders = useCallback(async () => {
    if (!orgId || !userId) return;
    setFoldersError(null);
    setLoadingFolders(true);
    try {
      // ✅ [최적화] 폴더 목록 + 파일 목록을 한 번에 호출
      const res = await axios.get<ApiResponse<FolderWithFiles[]>>(
        `${API_BASE}folders/structure`,
        { params: { org_id: orgId }, headers: getHeaders() }
      );
      if (!res.data.success) throw new Error(res.data.error || "Failed to load folders");

      // FolderWithFiles[]: 각 폴더 안에 files: [] 가 포함되어 있음
      const allRows = res.data.data || [];

      // [분류 로직]
      const globalList = allRows.filter((f) => f.org_id === '#GLOBAL#');
      
      let orgList: FolderRow[] = [];
      let myList: FolderRow[] = [];

      if (isSuperAdmin) {
        // 슈퍼 관리자: GLOBAL이 곧 내 조직. 나머지는 빈 목록.
        orgList = [];
        myList = [];
      } else if (isOrgAdmin) {
        // 일반 관리자: 내 것 -> 조직 공용
        orgList = allRows.filter((f) => f.org_id === orgId && f.created_by === userId);
      } else {
        // 팀원: 남 것 -> 조직 공용, 내 것 -> 내 팀
        orgList = allRows.filter((f) => f.org_id === orgId && f.created_by !== userId);
        myList = allRows.filter((f) => f.org_id === orgId && f.created_by === userId);
      }

      setGlobalFolders(globalList);
      setOrgFolders(orgList);
      setMyFolders(myList);

      // ✅ [최적화] 파일 목록을 filesMap으로 변환 (N+1 호출 제거)
      const fileMap: Record<string, FileRow[]> = {};
      allRows.forEach(folder => {
        fileMap[folder.folder_id] = folder.files || [];
      });
      setFilesMap(fileMap);

    } catch (e: any) {
      setFoldersError(e?.message);
      setGlobalFolders([]); setOrgFolders([]); setMyFolders([]); setFilesMap({});
    } finally {
      setLoadingFolders(false);
    }
  }, [orgId, userId, getHeaders, isOrgAdmin, isSuperAdmin]);

  useEffect(() => {
    if (orgId) loadAllFolders();
  }, [orgId, loadAllFolders]);

  // --- Tree Logic ---
  const buildTree = (folders: FolderRow[]): FolderNode[] => {
    const map = new Map<string, FolderNode>();
    const roots: FolderNode[] = [];
    folders.forEach((f) => map.set(f.folder_id, { ...f, children: [] }));
    folders.forEach((f) => {
      const node = map.get(f.folder_id)!;
      const pid = f.parent_folder_id;
      if (pid && map.has(pid)) map.get(pid)!.children.push(node);
      else roots.push(node);
    });
    const sortRec = (nodes: FolderNode[]) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name));
      nodes.forEach((n) => sortRec(n.children));
    };
    sortRec(roots);
    return roots;
  };

  const globalTree = useMemo(() => buildTree(globalFolders), [globalFolders]);
  const orgTree = useMemo(() => buildTree(orgFolders), [orgFolders]);
  const myTree = useMemo(() => buildTree(myFolders), [myFolders]);

  const collectAllFolderIds = (node: FolderNode): string[] => {
    const list = [node.folder_id];
    node.children.forEach((c) => list.push(...collectAllFolderIds(c)));
    return list;
  };
  const collectAllFileIds = (folderIds: string[]): string[] => {
    const list: string[] = [];
    folderIds.forEach((fid) => {
      const files = filesMap[fid] || [];
      files.forEach((f) => list.push(f.file_id));
    });
    return list;
  };

  const toggleFolder = (node: FolderNode, checked: boolean) => {
    if (!bundleEditable) return; 
    const allFolderIds = collectAllFolderIds(node);
    const allFileIds = collectAllFileIds(allFolderIds);
    const nextFolders = new Set(selectedFolders);
    const nextFiles = new Set(selectedFiles);

    if (checked) {
      allFolderIds.forEach((id) => nextFolders.add(id));
      allFileIds.forEach((id) => nextFiles.add(id));
    } else {
      allFolderIds.forEach((id) => nextFolders.delete(id));
      allFileIds.forEach((id) => nextFiles.delete(id));
    }
    setSelectedFolders(nextFolders);
    setSelectedFiles(nextFiles);
  };

  const toggleFile = (fileId: string, checked: boolean) => {
    if (!bundleEditable) return;
    const nextFiles = new Set(selectedFiles);
    if (checked) nextFiles.add(fileId);
    else nextFiles.delete(fileId);
    setSelectedFiles(nextFiles);
  };

  // --- Editor Handlers ---
  const resetEditor = () => {
    setEditingBundleId(null);
    setBundleName("");
    setBundleDescription("");
    setSelectedFolders(new Set());
    setSelectedFiles(new Set());
    setBundleEditable(true);
  };

  const handleStartNewBundle = () => {
    resetEditor();
  };

  const handleSelectBundle = async (bundle: SearchBundleListRow) => {
    try {
      const res = await axios.get<ApiResponse<SearchBundleDetailResponse>>(
        `${API_BASE}search-bundles/${bundle.bundle_id}`,
        { headers: getHeaders() }
      );
      if (!res.data.success || !res.data.data) {
        throw new Error(res.data.error || "Failed to load bundle");
      }
      const detail = res.data.data;
      const b = detail.bundle;
      
      const realBundleId = b.id || b.bundle_id;
      if (!realBundleId) throw new Error("Invalid bundle ID from server");

      const resources = detail.resources || [];

      const folderIds = new Set<string>();
      const fileIds = new Set<string>();

      resources.forEach((r) => {
        if (r.resource_type === 'folder' && r.folder_id) folderIds.add(r.folder_id);
        if (r.resource_type === 'file' && r.file_id) fileIds.add(r.file_id);
      });

      setEditingBundleId(realBundleId);
      setBundleName(b.name || "");
      setBundleDescription(b.description || "");
      setSelectedFolders(folderIds);
      setSelectedFiles(fileIds);
      setBundleEditable(bundle.is_editable);
      
    } catch(e) {
      console.error(e);
      alert("번들 정보를 불러오지 못했습니다.");
    }
  };

  const handleDeleteBundle = async (bundle: SearchBundleListRow) => {
    if (!window.confirm(`검색 번들 "${bundle.name}" 을(를) 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API_BASE}search-bundles/${bundle.bundle_id}`, { headers: getHeaders() });
      if (editingBundleId === bundle.bundle_id) resetEditor();
      loadBundles();
    } catch(e) { alert("삭제 실패했습니다."); }
  };

  // --- Tree Render Helper ---
  const renderNode = (node: FolderNode, depth: number) => {
    const folderChecked = selectedFolders.has(node.folder_id);
    const files = filesMap[node.folder_id] || [];
    const isDisabled = !bundleEditable;

    return (
      <Box key={node.folder_id} sx={{ ml: depth * 2, py: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minHeight: 36 }}>
          <Checkbox
            checked={folderChecked}
            disabled={isDisabled}
            onChange={(e) => toggleFolder(node, e.target.checked)}
            size="small"
            sx={{ p: 0.5 }}
          />
          <FolderIcon color={folderChecked ? "primary" : "inherit"} sx={{ mt: 0.2 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Tooltip title={node.description || ""} placement="top-start">
              <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {node.name}
              </Typography>
            </Tooltip>
          </Box>
        </Stack>

        {files.map((file) => (
          <Box key={file.file_id} sx={{ ml: 4, mt: 0.3 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                checked={selectedFiles.has(file.file_id)}
                disabled={isDisabled}
                onChange={(e) => toggleFile(file.file_id, e.target.checked)}
                size="small"
                sx={{ p: 0.5 }}
              />
              <DescriptionIcon fontSize="small" color="action" />
              <Tooltip title={file.original_name}>
                <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200 }}>
                  {file.original_name}
                </Typography>
              </Tooltip>
            </Stack>
          </Box>
        ))}

        {node.children.map((child) => renderNode(child, depth + 1))}
      </Box>
    );
  };

  // --- SAVE Handler ---
  const handleSave = async () => {
    if (!orgId) return alert("조직 정보 오류");
    const folders = Array.from(selectedFolders);
    const files = Array.from(selectedFiles);
    
    if (folders.length === 0 && files.length === 0) {
      return alert("하나 이상의 폴더나 파일을 선택해주세요.");
    }

    const basePayload: any = { selected_folders: folders, selected_files: files };
    if (bundleName.trim()) basePayload.name = bundleName.trim();
    if (bundleDescription.trim()) basePayload.description = bundleDescription.trim();

    try {
      if (editingBundleId) {
        await axios.put<ApiResponse>(
          `${API_BASE}search-bundles/${editingBundleId}`,
          basePayload,
          { headers: getHeaders() }
        );
        alert("수정되었습니다.");
      } else {
        const res = await axios.post<ApiResponse>(
          `${API_BASE}search-bundles`,
          basePayload,
          { headers: getHeaders() }
        );
        if (res.data.success) {
          alert("생성되었습니다.");
          resetEditor();
        }
      }
      loadBundles();
    } catch (e: any) {
      console.error(e);
      alert("저장 실패");
    }
  };

  const selectedFolderCount = selectedFolders.size;
  const selectedFileCount = selectedFiles.size;
  const savingDisabled = !bundleEditable || (selectedFolderCount === 0 && selectedFileCount === 0);
  const isEditMode = !!editingBundleId;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 2, boxSizing: 'border-box', overflow: 'hidden', backgroundColor: theme.palette.background.default }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexShrink: 0 }}>
        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate("/")}>
          대시보드
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 800, ml: 1 }}>
          검색 번들 관리
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => { loadBundles(); if (orgId) loadAllFolders(); }}>
          새로고침
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0 }}>
        {/* Left: Bundle List */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                검색 번들 목록
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" variant="contained" onClick={handleStartNewBundle} startIcon={<AddIcon />}>
                새 번들
              </Button>
            </Stack>

            {bundleError && <Alert severity="error" sx={{ mb: 1 }}>{bundleError}</Alert>}

            {loadingBundles ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
              <ScrollHiddenBox>
                {bundles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    생성된 번들이 없습니다.
                  </Typography>
                ) : (
                  <List dense>
                    {bundles.map((b) => (
                      <React.Fragment key={b.bundle_id}>
                        <ListItemButton
                          selected={editingBundleId === b.bundle_id}
                          onClick={() => handleSelectBundle(b)}
                          sx={{ 
                            borderRadius: 1, 
                            mb: 0.5,
                            border: editingBundleId === b.bundle_id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{b.name}</Typography>
                                {!b.is_editable && <Chip label="읽기 전용" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                              </Stack>
                            }
                            secondary={
                              <Box component="span">
                                <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                  {b.description || "설명 없음"}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                  <Chip size="small" label={`폴더 ${b.folder_count} · 파일 ${b.file_count}`} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                  <Typography variant="caption" color="text.disabled">
                                    {formatDate(b.updated_at)}
                                  </Typography>
                                </Stack>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            {b.is_editable && (
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="편집">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSelectBundle(b); }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="삭제">
                                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteBundle(b); }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </ListItemSecondaryAction>
                        </ListItemButton>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </ScrollHiddenBox>
            )}
          </Paper>
        </Grid>

        {/* Right: Editor */}
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Paper sx={{ p: 2, height: '100%', overflow: "hidden", display: "flex", flexDirection: "column", gap: 2 }}>
            
            {/* Editor Header */}
            <Box sx={{ flexShrink: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                {isEditMode ? (
                  <Chip label="편집 모드" color="primary" size="small" />
                ) : (
                  <Chip label="생성 모드" color="success" size="small" />
                )}
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {isEditMode ? bundleName || "이름 없는 번들" : "새 검색 번들 만들기"}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isEditMode && (
                  <Button variant="outlined" size="small" onClick={resetEditor} color="inherit">
                    편집 취소 (새로 만들기)
                  </Button>
                )}
              </Stack>

              <Stack spacing={2}>
                <TextField 
                  label="번들 이름" 
                  size="small" 
                  fullWidth 
                  value={bundleName} 
                  onChange={(e) => setBundleName(e.target.value)} 
                  disabled={!bundleEditable}
                  placeholder="예: 2024년 프로젝트 자료 모음"
                />
                <TextField 
                  label="설명" 
                  size="small" 
                  fullWidth 
                  multiline 
                  minRows={2} 
                  maxRows={3} 
                  value={bundleDescription} 
                  onChange={(e) => setBundleDescription(e.target.value)} 
                  disabled={!bundleEditable}
                  placeholder="이 번들에 대한 설명을 입력하세요."
                />
              </Stack>
            </Box>

            <Divider />

            {/* Selection Area */}
            <ScrollHiddenBox sx={{ mt: 1, pr: 1 }}>
              {loadingFolders ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : (
                <Stack spacing={1}>
                  {/* 1. 전체 공용 (항상 맨 위) */}
                  <Accordion expanded={expandGlobal} onChange={() => setExpandGlobal(!expandGlobal)} disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PublicIcon color="disabled" fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>전체 공용</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {globalTree.length === 0 ? <Typography variant="caption" color="text.secondary">폴더가 없습니다.</Typography> : globalTree.map(node => renderNode(node, 0))}
                    </AccordionDetails>
                  </Accordion>

                  {/* 2. 조직 공용 (슈퍼 관리자는 안 보임) */}
                  {!isSuperAdmin && (
                    <Accordion expanded={expandOrg} onChange={() => setExpandOrg(!expandOrg)} disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DomainIcon color="action" fontSize="small" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>조직 공용</Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {orgTree.length === 0 ? <Typography variant="caption" color="text.secondary">폴더가 없습니다.</Typography> : orgTree.map(node => renderNode(node, 0))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* 3. 내 팀 (Tier 1만 보임) */}
                  {!isOrgAdmin && (
                    <Accordion expanded={expandMy} onChange={() => setExpandMy(!expandMy)} disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>내 팀</Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {myTree.length === 0 ? <Typography variant="caption" color="text.secondary">폴더가 없습니다.</Typography> : myTree.map(node => renderNode(node, 0))}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Stack>
              )}
            </ScrollHiddenBox>

            {/* Footer Actions */}
            <Box sx={{ flexShrink: 0, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  선택됨: <b>{selectedFolderCount}</b> 폴더, <b>{selectedFileCount}</b> 파일
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" onClick={resetEditor}>초기화</Button>
                <Button 
                  variant="contained" 
                  startIcon={<SaveIcon />} 
                  onClick={handleSave} 
                  disabled={savingDisabled}
                  color={isEditMode ? "primary" : "success"}
                >
                  {isEditMode ? "수정사항 저장" : "새 번들 생성"}
                </Button>
              </Stack>
            </Box>

          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchBundlePage;